import { useEffect, useMemo, useState } from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts'
import {
  CurrencyEur, Leaf, ArrowsClockwise, FileCsv, FilePdf, Info,
  Warning, FloppyDisk, TrendUp, ChartBar,
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV, openPrintWindow } from '../esg/exportHelpers'

const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

// Precios de referencia España 2024-2025 · fuentes oficiales
// - Electricidad: precio medio PVPC 2024 (REE)
// - Agua: tarifa media urbana España (Aqualia/Canal Isabel II referencia)
// - Gas natural: precio medio PVPC (CNMC)
// - District heating: media convenio urbano ES
const PRICE_ELECTRICITY_EUR_KWH = 0.18
const PRICE_WATER_EUR_M3 = 2.5
const PRICE_GAS_EUR_KWH = 0.08
const PRICE_DISTRICT_HEAT_EUR_KWH = 0.09

// ── Benchmarks OFICIALES de consumo BASE (edificio convencional) ──
// FUENTE PRINCIPAL: MITECO/AVEN (Agencia Valenciana de la Energía)
// "Guía de Ahorro y Eficiencia Energética en Establecimientos Hoteleros"
// pág. 35 · sección 3.2.3 · tabla "PARÁMETROS DE EFICIENCIA EN HOTELES"
// URL: https://www.miteco.gob.es/content/dam/miteco/es/cambio-climatico/
//       planes-y-estrategias/GuiaEficienciaHoteles_CV_tcm30-70383.pdf
// PDF local: /home/gisell/Hotel Qgat/BENCHMARKS_IDAE/
//            MITECO_Guia_Eficiencia_Hoteles_CV.pdf
//
// La tabla oficial da rangos "Excelente / Buena / Pobre / Deficiente" por
// tamaño de hotel (A grandes >150hab · B medios 50-150hab · C pequeños <50).
// Usamos el rango "Pobre" (media) como escenario base convencional: es lo
// que consumiría un hotel estándar sin medidas verdes.
const HOTEL_BENCHMARKS = {
  HotelGrande: {
    label: 'Hotel grande (>150 hab con lavandería + piscina)',
    tamanoRango: '>150 habitaciones',
    energiaKwhM2AnoElec: 225,  // rango Pobre 200-250
    energiaKwhM2AnoTerm: 270,  // rango Pobre 240-300 (combustibles)
    energiaKwhM2AnoTotal: 495, // rango Pobre 440-550
    aguaM3PlazaAno: 300,       // rango Pobre 280-320
    fuente: 'MITECO/AVEN Guía Hoteles CV · pág. 35 tabla A',
    fuenteUrl: 'https://www.miteco.gob.es/content/dam/miteco/es/cambio-climatico/planes-y-estrategias/GuiaEficienciaHoteles_CV_tcm30-70383.pdf',
  },
  HotelMedio: {
    label: 'Hotel medio (50-150 hab)',
    tamanoRango: '50-150 habitaciones',
    energiaKwhM2AnoElec: 105,  // rango Pobre 90-120
    energiaKwhM2AnoTerm: 245,  // rango Pobre 230-260
    energiaKwhM2AnoTotal: 350, // rango Pobre 320-380
    aguaM3PlazaAno: 202,       // rango Pobre 185-220
    fuente: 'MITECO/AVEN Guía Hoteles CV · pág. 35 tabla B',
    fuenteUrl: 'https://www.miteco.gob.es/content/dam/miteco/es/cambio-climatico/planes-y-estrategias/GuiaEficienciaHoteles_CV_tcm30-70383.pdf',
  },
  HotelPequeno: {
    label: 'Hotel pequeño (<50 hab)',
    tamanoRango: '<50 habitaciones',
    energiaKwhM2AnoElec: 90,   // rango Pobre 80-100
    energiaKwhM2AnoTerm: 225,  // rango Pobre 210-240
    energiaKwhM2AnoTotal: 315, // rango Pobre 290-340
    aguaM3PlazaAno: 150,       // rango Pobre 140-160
    fuente: 'MITECO/AVEN Guía Hoteles CV · pág. 35 tabla C',
    fuenteUrl: 'https://www.miteco.gob.es/content/dam/miteco/es/cambio-climatico/planes-y-estrategias/GuiaEficienciaHoteles_CV_tcm30-70383.pdf',
  },
  Oficina: {
    label: 'Oficina',
    tamanoRango: 'N/A',
    energiaKwhM2AnoElec: 210,
    energiaKwhM2AnoTerm: 0,
    energiaKwhM2AnoTotal: 210,
    aguaM3PlazaAno: null,       // el benchmark hídrico agua/plaza no aplica a oficinas
    fuente: 'IDAE · pendiente cargar PDF sector terciario oficinas',
    fuenteUrl: 'https://www.idae.es/',
  },
}

// Selección automática de benchmark hotelero según nº habitaciones.
function seleccionarBenchmarkHotel(numHabitaciones) {
  if (numHabitaciones == null) return HOTEL_BENCHMARKS.HotelMedio  // asunción segura
  if (numHabitaciones > 150) return HOTEL_BENCHMARKS.HotelGrande
  if (numHabitaciones < 50) return HOTEL_BENCHMARKS.HotelPequeno
  return HOTEL_BENCHMARKS.HotelMedio
}

// Datos específicos del proyecto QGAT (fuente: CARTA BAILORULL pág. 2)
// Se leerán del proyecto en Supabase cuando el schema soporte estos
// campos; por ahora los tratamos como hardcode conocido con la fuente.
const HOTEL_META_POR_PROYECTO = {
  'd6507a89-c84a-46a6-b171-f7625946ac98': {
    numHabitaciones: 71,
    plazasMaximas: 142,
    ocupacionMedia: 0.60,   // INE hoteles urbanos 2024
    fuenteHabitaciones: 'CARTA BAILORULL pág. 2 sección 3.(a)',
    fuenteOcupacion: 'INE Encuesta Ocupación Hotelera España 2024',
    tieneGas: false,        // 100% eléctrico VRV+FV
  },
}


// ── Benchmarks financieros por categoría de medida verde ───────
// Solo se usa el sobrecostePct (para el CapEx incremental). El ahorro
// anual ya no se calcula como % del OpEx real (que sería contar dos veces
// el ahorro), sino como diferencia OpEx_base_IDAE − OpEx_real_medido.
// Ver bloque financialBreakdown más abajo.
//
// FUENTE de los % de sobrecoste: CYPE Generador de Precios
// (generadordeprecios.info) · comparativa verde vs base equivalente.
const BENCHMARKS_VERDES = {
  energia_renovable: {
    sobrecostePct: 1.00,   // FV/aerotermia son inversión nueva (no hay alternativa "no verde")
    ahorroTipo: 'electricidad',
    ahorroPct: 0.30,       // FV cubre ~30% de la factura eléctrica en hotel medio
    label: 'Energía renovable (FV, aerotermia)',
    paybackTipico: '7-10 años',
  },
  alta_eficiencia_energetica: {
    sobrecostePct: 0.40,   // LED ~25% + recuperador entálpico ~150% (Daikin VAM500 4949 vs vent. simple 2000) → media ponderada 40%
    ahorroTipo: 'electricidad',
    ahorroPct: 0.40,       // LED+DALI+recuperador reducen 40% consumo eléctrico
    label: 'Alta eficiencia (LED, DALI, BMS, recuperador)',
    paybackTipico: '3-5 años',
  },
  eficiencia_hidrica: {
    sobrecostePct: 1.00,   // Aguas grises/pluviales son sistemas NUEVOS (no hay alternativa)
    ahorroTipo: 'agua',
    ahorroPct: 0.35,       // reduce 35% consumo agua potable
    label: 'Eficiencia hídrica (aguas grises, pluviales, grifería <6L/min)',
    paybackTipico: '6-8 años',
  },
  mejora_termica_envolvente: {
    sobrecostePct: 0.15,   // XPS/MW: precios ≈ base CYPE · SATE lana vs EPS 15% · fachada NZEB vs convencional 20%
    ahorroTipo: 'electricidad', // clima
    ahorroPct: 0.20,       // 20% ahorro en climatización por CTE-HE1 mejorado
    label: 'Mejora térmica envolvente (aislamiento, vidrios U/g)',
    paybackTipico: '10-15 años',
  },
  certificacion_ambiental: {
    sobrecostePct: 0.20,   // Pintura ecológica vs base CYPE 21% · Tabique Knauf certif vs base 23%
    ahorroTipo: null,      // no reduce OpEx directamente
    ahorroPct: 0,
    label: 'Certificación ambiental (EPD, FSC, PEFC, Ecolabel)',
    paybackTipico: 'Retorno no financiero · huella C + puntos BREEAM',
  },
  material_bajo_carbono: {
    sobrecostePct: 0.15,   // CEM III/madera FSC ~15% sobrecoste (típico literatura española)
    ahorroTipo: null,
    ahorroPct: 0,
    label: 'Material bajo carbono (CEM III/V, acero reciclado)',
    paybackTipico: 'Retorno no financiero · huella C',
  },
  movilidad_sostenible: {
    sobrecostePct: 1.00,   // puntos VE/aparcabicis son inversión nueva
    ahorroTipo: null,
    ahorroPct: 0,
    label: 'Movilidad sostenible (carga VE, aparcabicis)',
    paybackTipico: 'Retorno no financiero · prima activo VE',
  },
  biodiversidad: {
    sobrecostePct: 0.25,   // Trasplantes + cubierta vegetal vs paisajismo estándar 25%
    ahorroTipo: null,
    ahorroPct: 0,
    label: 'Biodiversidad (cubierta vegetal, autóctonas)',
    paybackTipico: 'Retorno no financiero · huella C + biodiversidad',
  },
}

// Etiquetas legibles de capítulos constructivos
const CAPITULOS_LABEL = {
  '01_movimiento_tierras': '01 Movimiento de tierras',
  '02_cimentacion':        '02 Cimentación',
  '03_estructura':         '03 Estructura',
  '04_cubiertas':          '04 Cubiertas',
  '05_cerramientos':       '05 Cerramientos',
  '06_particiones':        '06 Particiones',
  '07_acabados':           '07 Acabados',
  '08_instalaciones':      '08 Instalaciones',
  '09_urbanizacion':       '09 Urbanización',
  '10_seguridad':          '10 Seguridad',
  '11_control_calidad':    '11 Control de calidad',
  '12_gestion_residuos':   '12 Gestión de residuos',
  '13_otros':              '13 Otros',
}
const CAPITULOS_ORDER = Object.keys(CAPITULOS_LABEL)

const capituloLabel = (c) => CAPITULOS_LABEL[c] || (c || 'Sin clasificar')

// Formatea importe en euros con separador de miles ES
const fmtEur = (v) => v == null ? '—' : `${Math.round(v).toLocaleString('es-ES')} €`
const fmtPct = (v) => v == null ? '—' : `${v.toFixed(1)} %`

/**
 * CapexOpex V2 — análisis financiero real desde partidas del presupuesto.
 * No usa proxies. Todos los importes vienen de partidas extraídas del PDF
 * valorado subido en el Cockpit del Contratista (tipo `presupuesto_capex`).
 */
export default function CapexOpex({ project }) {
  const [partidas, setPartidas] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sensors, setSensors] = useState(null)
  const [subfaseFilter, setSubfaseFilter] = useState('todas')
  const [discountRate, setDiscountRate] = useState(5)
  const [savedMsg, setSavedMsg] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [loadedAt, setLoadedAt] = useState(null)

  const projectId = project?.id
  const surface = project?.surface_m2 || 0

  const loadData = async () => {
    if (!projectId) return
    setLoading(true); setError(null)
    // 1. Documentos presupuesto_capex del Cockpit con sus partidas ya analizadas
    // 2. Sensors para OpEx real
    // 3. Snapshot previo (Prima €/m², etc.)
    const [
      { data: docs, error: e1 },
      { data: sRows },
      { data: snap },
    ] = await Promise.all([
      supabase.from('cc_documentos_proyecto')
        .select('id, tipo, nombre, notas_extraccion, partidas_detectadas, analisis_estado')
        .eq('project_id', projectId)
        .eq('tipo', 'presupuesto_capex')
        .eq('analisis_estado', 'completado'),
      supabase.from('building_sensors').select('*').eq('project_id', projectId).order('reading_year', { ascending: false }),
      supabase.from('esg_snapshots')
        .select('source_data, created_at')
        .eq('project_id', projectId).eq('framework', 'capex_opex_v2')
        .order('created_at', { ascending: false }).limit(1),
    ])
    setLoading(false)
    if (e1) { setError(e1.message); return }

    // Aplanar todas las partidas de todos los documentos analizados. Cada partida
    // arrastra la subfase del documento (guardada en notas_extraccion).
    const flat = []
    ;(docs || []).forEach(doc => {
      // Subfase declarada al subir (metadata del doc)
      let docSubfase = null
      try { docSubfase = JSON.parse(doc.notas_extraccion || 'null')?.subfase || null } catch {}
      const rows = doc.partidas_detectadas || []
      rows.forEach(p => {
        // El AI extrae "partidas" con array de "materiales"; en presupuesto_capex
        // cada partida ya es una línea única con precio, así que la usamos directa.
        // Fallback: si viene con materiales anidados, extraerlos como filas.
        if (Array.isArray(p.materiales) && p.materiales.length > 0) {
          p.materiales.forEach(m => {
            flat.push({
              docId: doc.id,
              docNombre: doc.nombre,
              codigo: p.codigo || m.codigo || null,
              descripcion: m.nombre || p.nombre_partida || '',
              cantidad: m.cantidad ?? null,
              unidad: m.unidad || null,
              precio_unitario_eur: m.precio_unitario_eur ?? null,
              importe_total_eur: m.importe_total_eur ?? null,
              capitulo_constructivo: m.capitulo_constructivo || p.capitulo_constructivo || '13_otros',
              sistema: m.sistema || p.sistema || 'Otros',
              es_sostenible: m.es_sostenible ?? false,
              criterio_sostenibilidad: m.criterio_sostenibilidad || null,
              evidencia_literal: m.evidencia_literal || null,
              descripcion_sostenibilidad: m.descripcion_sostenibilidad || null,
              subfase: m.subfase || p.subfase || docSubfase || null,
              pagina: p.pagina ?? null,
            })
          })
        } else {
          flat.push({
            docId: doc.id,
            docNombre: doc.nombre,
            codigo: p.codigo || null,
            descripcion: p.descripcion || p.nombre_partida || '',
            cantidad: p.cantidad ?? null,
            unidad: p.unidad || null,
            precio_unitario_eur: p.precio_unitario_eur ?? null,
            importe_total_eur: p.importe_total_eur ?? null,
            capitulo_constructivo: p.capitulo_constructivo || '13_otros',
            sistema: p.sistema || 'Otros',
            es_sostenible: p.es_sostenible ?? false,
            criterio_sostenibilidad: p.criterio_sostenibilidad || null,
            evidencia_literal: p.evidencia_literal || null,
            descripcion_sostenibilidad: p.descripcion_sostenibilidad || null,
            subfase: p.subfase || docSubfase || null,
            pagina: p.pagina ?? null,
          })
        }
      })
    })
    setPartidas(flat)

    // Sensores anuales · sumamos consumos (kWh, m³) y también importes reales
    // de factura (€) cuando el asesor los ha cargado. Cada vector lleva su
    // contador de meses con importe real para saber cuántos faltan.
    if (sRows?.length) {
      const yMap = new Map()
      sRows.forEach(r => {
        const a = yMap.get(r.reading_year) || {
          year: r.reading_year,
          kwhTotal: 0, kwhElec: 0, kwhGas: 0, kwhDH: 0, m3: 0,
          eurElec: 0, eurGas: 0, eurDH: 0, eurAgua: 0,
          mesesConElec: 0, mesesConGas: 0, mesesConDH: 0, mesesConAgua: 0,
          mesesElecConEur: 0, mesesGasConEur: 0, mesesDHConEur: 0, mesesAguaConEur: 0,
        }
        const elec = +(r.electricity_kwh || 0)
        const gas = +(r.gas_kwh || 0)
        const dh = +(r.district_heat_kwh || 0)
        const m3 = +(r.water_m3_total || 0)
        const totalVectores = elec + gas + dh
        if (totalVectores > 0) {
          a.kwhElec += elec
          a.kwhGas += gas
          a.kwhDH += dh
          a.kwhTotal += totalVectores
        } else {
          a.kwhTotal += +(r.energy_kwh_total || 0)
          a.kwhElec += +(r.energy_kwh_total || 0)
        }
        a.m3 += m3
        // Contadores de meses con dato de consumo
        if (elec > 0) a.mesesConElec += 1
        if (gas > 0) a.mesesConGas += 1
        if (dh > 0) a.mesesConDH += 1
        if (m3 > 0) a.mesesConAgua += 1
        // Importes REALES de factura (si están cargados)
        if (r.electricity_eur != null) { a.eurElec += +r.electricity_eur; a.mesesElecConEur += 1 }
        if (r.gas_eur != null) { a.eurGas += +r.gas_eur; a.mesesGasConEur += 1 }
        if (r.district_heat_eur != null) { a.eurDH += +r.district_heat_eur; a.mesesDHConEur += 1 }
        if (r.water_eur != null) { a.eurAgua += +r.water_eur; a.mesesAguaConEur += 1 }
        yMap.set(r.reading_year, a)
      })
      const years = [...yMap.keys()].sort((a, b) => b - a)
      setSensors(yMap.get(years[0]))
    }

    // Snapshot previo (asesor guardó Prima o discount rate en sesión anterior)
    if (snap?.[0]?.source_data) {
      const sd = snap[0].source_data
      if (sd.discountRate != null) setDiscountRate(sd.discountRate)
      setLoadedAt(snap[0].created_at)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData() }, [projectId])

  // ── Filtrado por subfase ──
  const partidasFiltradas = useMemo(() => {
    if (subfaseFilter === 'todas') return partidas
    if (subfaseFilter === 'sin_subfase') return partidas.filter(p => !p.subfase)
    return partidas.filter(p => p.subfase === subfaseFilter)
  }, [partidas, subfaseFilter])

  // ── Subfases detectadas para el selector ──
  const subfasesDetectadas = useMemo(() => {
    const s = new Set()
    partidas.forEach(p => { if (p.subfase) s.add(p.subfase) })
    return [...s].sort()
  }, [partidas])

  // ── Detectar tipología + superficie GFA desde MAT1-DATA ──
  // Se declaran aquí (antes del useMemo de kpi) porque effectiveSurfaceForKpi
  // depende de gfaFromMat1. Si se declararan más abajo, en build minificado
  // se produce ReferenceError "Cannot access before initialization".
  const [buildingType, setBuildingType] = useState(null)
  const [gfaFromMat1, setGfaFromMat1] = useState(null)
  useEffect(() => {
    if (!projectId) return
    supabase.from('evaluations')
      .select('justificacion')
      .eq('project_id', projectId)
      .eq('criteria_code', 'MAT1-DATA')
      .maybeSingle()
      .then(({ data }) => {
        try {
          const j = JSON.parse(data?.justificacion || '{}')
          setBuildingType(j.buildingType || null)
          if (j.gfa) setGfaFromMat1(parseFloat(j.gfa))
        } catch { /* ignore */ }
      })
  }, [projectId])

  // Superficie efectiva: usar la del project, o el GFA de MAT1-DATA como
  // fallback (en Supabase surface_m2 puede venir NULL en proyectos antiguos).
  const effectiveSurfaceForKpi = surface || gfaFromMat1 || 0

  // ── KPIs globales ──
  const kpi = useMemo(() => {
    const total = partidasFiltradas.reduce((s, p) => s + (p.importe_total_eur || 0), 0)
    const green = partidasFiltradas.filter(p => p.es_sostenible)
      .reduce((s, p) => s + (p.importe_total_eur || 0), 0)
    const greenPct = total > 0 ? (green / total) * 100 : 0
    const eurPerM2 = effectiveSurfaceForKpi > 0 ? total / effectiveSurfaceForKpi : null
    const nGreen = partidasFiltradas.filter(p => p.es_sostenible).length
    return { total, green, greenPct, eurPerM2, nGreen, nTotal: partidasFiltradas.length }
  }, [partidasFiltradas, effectiveSurfaceForKpi])

  // ── OpEx anual real desde sensors ──
  // Prioridad: usar importes REALES de factura cargados; si un vector no
  // tiene factura en algún mes, extrapolar con el precio unitario medio de
  // los meses SÍ facturados (más preciso que un precio nacional estimado).
  const opex = useMemo(() => {
    const kwhElec = sensors?.kwhElec || 0
    const kwhGas = sensors?.kwhGas || 0
    const kwhDH = sensors?.kwhDH || 0
    const m3 = sensors?.m3 || 0

    // Vector: usar € real cargado + estimar meses faltantes con precio propio
    const calcVector = (kwhOm3, eurReal, mesesConConsumo, mesesConEur, precioEstimado) => {
      if (kwhOm3 === 0) return { eur: 0, precioReal: null, mesesFactura: 0, mesesTotal: 0, estimado: false }
      if (mesesConEur === 0) {
        return { eur: kwhOm3 * precioEstimado, precioReal: null, mesesFactura: 0, mesesTotal: mesesConConsumo, estimado: true }
      }
      // Precio unitario REAL medio de los meses con factura
      const consumoConEur = (kwhOm3 * mesesConEur) / Math.max(mesesConConsumo, 1)  // aprox proporcional
      const precioReal = eurReal / Math.max(consumoConEur, 1)
      // Total = suma directa si todos los meses tienen factura; si no, extrapolar con precio real
      if (mesesConEur === mesesConConsumo) {
        return { eur: eurReal, precioReal, mesesFactura: mesesConEur, mesesTotal: mesesConConsumo, estimado: false }
      }
      // Aplicar precio real a todos los meses de consumo
      return { eur: kwhOm3 * precioReal, precioReal, mesesFactura: mesesConEur, mesesTotal: mesesConConsumo, estimado: false }
    }

    const elec = calcVector(kwhElec, sensors?.eurElec || 0, sensors?.mesesConElec || 0, sensors?.mesesElecConEur || 0, PRICE_ELECTRICITY_EUR_KWH)
    const gas = calcVector(kwhGas, sensors?.eurGas || 0, sensors?.mesesConGas || 0, sensors?.mesesGasConEur || 0, PRICE_GAS_EUR_KWH)
    const dh = calcVector(kwhDH, sensors?.eurDH || 0, sensors?.mesesConDH || 0, sensors?.mesesDHConEur || 0, PRICE_DISTRICT_HEAT_EUR_KWH)
    const agua = calcVector(m3, sensors?.eurAgua || 0, sensors?.mesesConAgua || 0, sensors?.mesesAguaConEur || 0, PRICE_WATER_EUR_M3)

    const total = elec.eur + gas.eur + dh.eur + agua.eur
    return {
      year: sensors?.year,
      electricidadKwh: kwhElec || null,
      gasKwh: kwhGas || null,
      districtHeatKwh: kwhDH || null,
      aguaM3: m3 || null,
      electricidadEur: elec.eur > 0 ? elec.eur : null,
      gasEur: gas.eur > 0 ? gas.eur : null,
      districtHeatEur: dh.eur > 0 ? dh.eur : null,
      aguaEur: agua.eur > 0 ? agua.eur : null,
      totalEur: total > 0 ? total : null,
      // Trazabilidad por vector
      elecDetail: elec, gasDetail: gas, dhDetail: dh, aguaDetail: agua,
    }
  }, [sensors])

  const effectiveSurface = effectiveSurfaceForKpi

  // Meta hotelero del proyecto (nº habitaciones + ocupación) para elegir bien
  // el benchmark MITECO y convertir agua m³/plaza·año a m³/año totales.
  const hotelMeta = HOTEL_META_POR_PROYECTO[projectId] || null

  const idaeBenchmark = useMemo(() => {
    if (buildingType === 'Oficina') return HOTEL_BENCHMARKS.Oficina
    if (buildingType === 'Hotel' || buildingType === 'Hotel 5*' || buildingType === 'Comercial') {
      return seleccionarBenchmarkHotel(hotelMeta?.numHabitaciones)
    }
    return HOTEL_BENCHMARKS.HotelMedio
  }, [buildingType, hotelMeta?.numHabitaciones])

  // ── OpEx BASE hipotético según MITECO/AVEN (hotel sin medidas verdes) ──
  // Se aplica el mismo precio unitario que paga el edificio real: si hay
  // factura, usar el €/kWh y €/m³ REAL del proyecto; si no, precio nacional.
  // Así se compara "peras con peras" (mismo comercializador y tarifas).
  const opexBase = useMemo(() => {
    if (!effectiveSurface || !idaeBenchmark) return null
    const elecKwh = idaeBenchmark.energiaKwhM2AnoElec * effectiveSurface
    const termKwh = idaeBenchmark.energiaKwhM2AnoTerm * effectiveSurface
    let aguaM3 = null
    if (hotelMeta && idaeBenchmark.aguaM3PlazaAno) {
      const plazasAno = hotelMeta.plazasMaximas * hotelMeta.ocupacionMedia
      aguaM3 = plazasAno * idaeBenchmark.aguaM3PlazaAno
    }
    // Precios reales del proyecto (si hay facturas cargadas) o estimados
    const precioElec = opex?.elecDetail?.precioReal || PRICE_ELECTRICITY_EUR_KWH
    const precioGas = opex?.gasDetail?.precioReal || PRICE_GAS_EUR_KWH
    const precioAgua = opex?.aguaDetail?.precioReal || PRICE_WATER_EUR_M3
    const elecEur = elecKwh * precioElec
    const termEur = termKwh * precioGas
    const aguaEur = aguaM3 != null ? aguaM3 * precioAgua : 0
    return {
      elecKwh, termKwh, aguaM3,
      elecEur, termEur, aguaEur,
      totalEur: elecEur + termEur + aguaEur,
      benchmark: idaeBenchmark,
      precioElecUsado: precioElec,
      precioAguaUsado: precioAgua,
      preciosRealesProyecto: !!(opex?.elecDetail?.precioReal || opex?.aguaDetail?.precioReal),
    }
  }, [effectiveSurface, idaeBenchmark, hotelMeta, opex?.elecDetail?.precioReal, opex?.gasDetail?.precioReal, opex?.aguaDetail?.precioReal])

  // ── Análisis financiero: ahorro REAL = base IDAE − consumo real ──
  const financialBreakdown = useMemo(() => {
    if (!kpi.green) return { rows: [], totalSobrecoste: 0, totalAhorro: 0, ahorroElec: 0, ahorroAgua: 0, payback: null, npv10: null, opexBaseTotal: null }

    // Sobrecoste por categoría (CapEx × sobrecostePct benchmark CYPE)
    const grupos = new Map()
    partidasFiltradas.forEach(p => {
      if (!p.es_sostenible) return
      const crit = p.criterio_sostenibilidad || 'certificacion_ambiental'
      if (!grupos.has(crit)) grupos.set(crit, { criterio: crit, capex: 0, n: 0 })
      const g = grupos.get(crit)
      g.capex += p.importe_total_eur || 0
      g.n += 1
    })

    // Ahorro REAL: OpEx_base_MITECO − OpEx_real. Se atribuye por vector (energía/agua).
    // El benchmark base incluye elec + combustibles (hotel convencional usa ambos).
    // El QGAT real solo tiene electricidad (100% eléctrico), así que la energía
    // real cuenta electricidad + gas + district heat (todo agregado).
    const opexRealElec = (opex.electricidadEur || 0) + (opex.gasEur || 0) + (opex.districtHeatEur || 0)
    const opexRealAgua = opex.aguaEur || 0
    const opexBaseElec = (opexBase?.elecEur || 0) + (opexBase?.termEur || 0)  // energía total base
    const opexBaseAgua = opexBase?.aguaEur || 0
    const ahorroElec = Math.max(0, opexBaseElec - opexRealElec)
    const ahorroAgua = Math.max(0, opexBaseAgua - opexRealAgua)
    const totalAhorro = ahorroElec + ahorroAgua

    // Total sobrecoste incremental (suma de todas las categorías)
    const rows = []
    let totalSobrecoste = 0
    const capexElec = { total: 0, sobre: 0 }  // suma CapEx de categorías con ahorro elec
    const capexAgua = { total: 0, sobre: 0 }  // idem agua

    grupos.forEach(g => {
      const bench = BENCHMARKS_VERDES[g.criterio] || BENCHMARKS_VERDES['certificacion_ambiental']
      const sobrecoste = g.capex * bench.sobrecostePct
      totalSobrecoste += sobrecoste
      if (bench.ahorroTipo === 'electricidad') {
        capexElec.total += g.capex
        capexElec.sobre += sobrecoste
      } else if (bench.ahorroTipo === 'agua') {
        capexAgua.total += g.capex
        capexAgua.sobre += sobrecoste
      }
      rows.push({
        criterio: g.criterio,
        label: bench.label,
        n: g.n,
        capex: g.capex,
        sobrecostePct: bench.sobrecostePct,
        sobrecoste,
        ahorroTipo: bench.ahorroTipo,
        paybackTipico: bench.paybackTipico,
      })
    })

    // Distribuir el ahorro real entre categorías proporcionalmente al CapEx
    rows.forEach(r => {
      if (r.ahorroTipo === 'electricidad' && capexElec.total > 0) {
        r.ahorroAnual = ahorroElec * (r.capex / capexElec.total)
      } else if (r.ahorroTipo === 'agua' && capexAgua.total > 0) {
        r.ahorroAnual = ahorroAgua * (r.capex / capexAgua.total)
      } else {
        r.ahorroAnual = 0
      }
      r.paybackCategoria = r.ahorroAnual > 0 ? r.sobrecoste / r.ahorroAnual : null
    })

    const payback = totalAhorro > 0 ? totalSobrecoste / totalAhorro : null
    let npv10 = null
    if (payback != null) {
      const r = discountRate / 100
      let v = -totalSobrecoste
      for (let t = 1; t <= 10; t++) v += totalAhorro / Math.pow(1 + r, t)
      npv10 = v
    }

    return {
      rows: rows.sort((a, b) => b.capex - a.capex),
      totalSobrecoste,
      totalAhorro,
      ahorroElec,
      ahorroAgua,
      opexBaseTotal: opexBase?.totalEur || null,
      opexBaseElec, opexBaseAgua,
      opexRealElec, opexRealAgua,
      payback,
      npv10,
    }
  }, [kpi.green, partidasFiltradas, opex.electricidadEur, opex.gasEur, opex.districtHeatEur, opex.aguaEur, opexBase, discountRate])

  // Alias para compatibilidad con el resto del código (opexChartData, saveState, etc.)
  const financial = useMemo(() => ({
    payback: financialBreakdown.payback,
    npv10: financialBreakdown.npv10,
    annualSaving: financialBreakdown.totalAhorro,
    totalSobrecoste: financialBreakdown.totalSobrecoste,
  }), [financialBreakdown])

  // ── CapEx por capítulo ──
  const porCapitulo = useMemo(() => {
    const m = new Map()
    partidasFiltradas.forEach(p => {
      const cap = p.capitulo_constructivo || '13_otros'
      if (!m.has(cap)) m.set(cap, { capitulo: cap, total: 0, green: 0, n: 0 })
      const r = m.get(cap)
      r.total += p.importe_total_eur || 0
      if (p.es_sostenible) r.green += p.importe_total_eur || 0
      r.n += 1
    })
    return CAPITULOS_ORDER
      .filter(c => m.has(c))
      .map(c => m.get(c))
      .concat([...m.values()].filter(r => !CAPITULOS_ORDER.includes(r.capitulo)))
  }, [partidasFiltradas])

  // ── CapEx por sistema (para gráfico de barras) ──
  const porSistema = useMemo(() => {
    const m = new Map()
    partidasFiltradas.forEach(p => {
      const sis = p.sistema || 'Otros'
      if (!m.has(sis)) m.set(sis, { sistema: sis, total: 0, green: 0 })
      const r = m.get(sis)
      r.total += p.importe_total_eur || 0
      if (p.es_sostenible) r.green += p.importe_total_eur || 0
    })
    return [...m.values()].sort((a, b) => b.total - a.total).slice(0, 12)
  }, [partidasFiltradas])

  // ── Comparativa A2 vs A3 ──
  const comparativaFases = useMemo(() => {
    const fasesConDatos = subfasesDetectadas.filter(sf =>
      partidas.some(p => p.subfase === sf))
    if (fasesConDatos.length < 2) return null
    const res = fasesConDatos.map(sf => {
      const filas = partidas.filter(p => p.subfase === sf)
      const total = filas.reduce((s, p) => s + (p.importe_total_eur || 0), 0)
      const green = filas.filter(p => p.es_sostenible).reduce((s, p) => s + (p.importe_total_eur || 0), 0)
      return {
        subfase: sf,
        total, green,
        greenPct: total > 0 ? (green / total) * 100 : 0,
        n: filas.length,
      }
    })
    return res
  }, [partidas, subfasesDetectadas])

  // ── Datos para el gráfico VAN ──
  // Aplica la tasa de descuento año a año: cada ahorro futuro se descuenta a
  // valor presente con fórmula VAN = Σ(ahorro / (1+r)^t). Así, cambiar la
  // tasa de descuento sí modifica la curva (a mayor tasa, curva más plana).
  const opexChartData = useMemo(() => {
    if (!financial.totalSobrecoste || !financial.annualSaving) return []
    const annual = financial.annualSaving
    const sobrecoste = financial.totalSobrecoste
    const r = (discountRate || 0) / 100
    const years = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20]
    return years.map(y => {
      let vanAcumulado = -sobrecoste
      for (let t = 1; t <= y; t++) {
        vanAcumulado += annual / Math.pow(1 + r, t)
      }
      return {
        year: `Año ${y}`,
        inversion: Math.round(vanAcumulado),
        referencia: 0,
      }
    })
  }, [financial.totalSobrecoste, financial.annualSaving, discountRate])

  // ── Guardado del snapshot (Prima €/m² editable) ──
  async function saveState() {
    setSaveError(null)
    if (!projectId) { setSaveError('Sin proyecto activo'); return }
    const { data: u } = await supabase.auth.getUser()
    const user_id = u?.user?.id
    if (!user_id) { setSaveError('No autenticado'); return }
    // Ahorro anual real (energía + agua) desde sensors
    const annual = financial.annualSaving || 0
    const { error } = await supabase.from('esg_snapshots').insert([{
      project_id: projectId, user_id,
      framework: 'capex_opex_v2',
      indicator_code: 'CAPEX_GREEN_TOTAL',
      indicator_name: 'CapEx verde total (analizado desde partidas)',
      value_numeric: kpi.green,
      value_unit: 'EUR',
      source_module: 'CapexOpexV2',
      source_data: {
        discountRate,
        capexTotal: kpi.total,
        capexGreen: kpi.green,
        greenPct: kpi.greenPct,
        sobrecosteIncrementalTotal: financial.totalSobrecoste,
        annualSavingTotal: annual,
        payback: financial.payback,
        npv10: financial.npv10,
        breakdownPorCategoria: financialBreakdown.rows,
        nPartidas: partidasFiltradas.length,
        nGreen: kpi.nGreen,
        subfaseFilter,
      },
    }])
    if (error) { setSaveError(error.message); return }
    setSavedMsg(true); setLoadedAt(new Date().toISOString())
    setTimeout(() => setSavedMsg(false), 2500)
  }

  // ── Exportaciones ──
  const handleCSV = () => {
    downloadCSV(
      partidasFiltradas.map(p => ({
        codigo: p.codigo, descripcion: p.descripcion,
        cantidad: p.cantidad, unidad: p.unidad,
        precio_unitario_eur: p.precio_unitario_eur,
        importe_total_eur: p.importe_total_eur,
        capitulo: capituloLabel(p.capitulo_constructivo),
        sistema: p.sistema, es_sostenible: p.es_sostenible ? 'Sí' : 'No',
        descripcion_sostenibilidad: p.descripcion_sostenibilidad,
        subfase: p.subfase || 'sin_subfase',
      })),
      `CapexOpexV2_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`
    )
  }

  const handlePDF = () => {
    openPrintWindow({
      title: 'Análisis Financiero CapEx / OpEx',
      projectName: project?.name || 'Proyecto',
      sections: [
        {
          title: 'Resumen',
          cols: ['KPI', 'Valor'],
          rows: [
            ['CapEx total analizado', fmtEur(kpi.total)],
            ['CapEx verde (medidas sostenibles)', fmtEur(kpi.green)],
            ['% sostenibilidad', fmtPct(kpi.greenPct)],
            ['CapEx € /m²', kpi.eurPerM2 != null ? fmtEur(kpi.eurPerM2) : '—'],
            ['Partidas verdes / total', `${kpi.nGreen} / ${kpi.nTotal}`],
            ['OpEx anual real', opex.totalEur != null ? fmtEur(opex.totalEur) : 'Sin sensores'],
            ['Sobrecoste incremental verde (benchmarks BREEAM)', financial.totalSobrecoste != null ? fmtEur(financial.totalSobrecoste) : '—'],
            ['Ahorro anual (por categoría)', financial.annualSaving != null ? fmtEur(financial.annualSaving) : '—'],
            ['Payback ponderado', financial.payback != null ? `${financial.payback.toFixed(1)} años` : '—'],
            ['VAN 10 años (descuento ' + discountRate + '%)', financial.npv10 != null ? fmtEur(financial.npv10) : '—'],
            ['Filtro subfase', subfaseFilter],
          ],
        },
        {
          title: 'CapEx por capítulo constructivo',
          cols: ['Capítulo', 'Partidas', 'CapEx total', 'CapEx verde', '% verde'],
          rows: porCapitulo.map(r => [
            capituloLabel(r.capitulo), r.n, fmtEur(r.total), fmtEur(r.green),
            r.total > 0 ? `${((r.green / r.total) * 100).toFixed(1)} %` : '—',
          ]),
        },
        ...(comparativaFases ? [{
          title: 'Comparativa por subfase',
          cols: ['Subfase', 'Partidas', 'CapEx total', 'CapEx verde', '% verde'],
          rows: comparativaFases.map(r => [
            r.subfase, r.n, fmtEur(r.total), fmtEur(r.green), fmtPct(r.greenPct),
          ]),
        }] : []),
      ],
    })
  }

  // ── UI ──
  const hasPartidas = partidas.length > 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>Análisis Financiero</div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600' }}>CapEx / OpEx · desde partidas del presupuesto</div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E', marginTop: '0.2rem' }}>
            Partidas: {partidas.length} · analizadas desde documentos "Presupuesto valorado (CapEx)" subidos al Cockpit del Contratista
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={loadData} disabled={loading} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: 5, padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <ArrowsClockwise size={13} /> {loading ? 'Cargando…' : 'Refrescar'}
          </button>
          <button onClick={saveState} style={{ background: savedMsg ? ACCENT.olive : 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: 5, padding: '6px 11px', fontSize: '0.72rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <FloppyDisk size={13} /> {savedMsg ? '✓ Guardado' : 'Guardar snapshot'}
          </button>
          <button onClick={handleCSV} disabled={!hasPartidas} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: 5, padding: '6px 11px', fontSize: '0.72rem', cursor: hasPartidas ? 'pointer' : 'not-allowed', opacity: hasPartidas ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <FileCsv size={13} /> CSV
          </button>
          <button onClick={handlePDF} disabled={!hasPartidas} style={{ background: 'rgba(245,240,234,0.1)', color: '#F5F0EA', border: '1px solid rgba(245,240,234,0.3)', borderRadius: 5, padding: '6px 11px', fontSize: '0.72rem', cursor: hasPartidas ? 'pointer' : 'not-allowed', opacity: hasPartidas ? 1 : 0.5, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <FilePdf size={13} /> PDF
          </button>
        </div>
      </div>

      {(error || saveError) && (
        <div style={{ color: ACCENT.coral, fontSize: '0.8rem', background: '#FDECE7', border: `1px solid ${ACCENT.coral}`, borderRadius: 5, padding: '0.55rem 0.85rem' }}>
          {error || saveError}
        </div>
      )}

      {loadedAt && <div style={{ fontSize: '0.7rem', color: '#7A6A5A' }}>Último snapshot: {new Date(loadedAt).toLocaleString('es-ES')}</div>}

      {/* Estado vacío */}
      {!hasPartidas && !loading && (
        <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.amber}` }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <Warning size={20} style={{ color: ACCENT.amber, flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: '0.85rem', color: '#5C4A3A', lineHeight: 1.6 }}>
              <strong style={{ color: '#2C2017' }}>Sin partidas para analizar.</strong> Este módulo lee las partidas de los documentos subidos al <strong>Cockpit del Contratista</strong> con tipo <strong>"Presupuesto valorado (CapEx)"</strong>. Para que el análisis financiero funcione:
              <ol style={{ marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                <li>Ve al Cockpit del Contratista de este proyecto</li>
                <li>Añade un documento nuevo tipo "Presupuesto valorado (CapEx)"</li>
                <li>Selecciona la subfase (A2, A3, o "sin subfase" para proyecto único)</li>
                <li>Sube el PDF del presupuesto valorado</li>
                <li>Pulsa "Analizar con IA" · Claude Haiku 4.5 extraerá partidas con precios y clasificación</li>
                <li>Vuelve aquí y refresca</li>
              </ol>
              <div style={{ marginTop: '0.55rem', fontSize: '0.78rem', color: '#7A6A5A', fontStyle: 'italic' }}>
                Este módulo es totalmente independiente de MAT 3. No modifica los datos usados para la certificación BREEAM.
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPartidas && (
        <>
          {/* Selector de subfase */}
          {(subfasesDetectadas.length > 0 || partidas.some(p => !p.subfase)) && (
            <div style={{ ...CARD, padding: '0.9rem 1.2rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', color: '#5C4A3A', fontWeight: 500 }}>Vista por subfase:</span>
                {['todas', ...subfasesDetectadas, ...(partidas.some(p => !p.subfase) ? ['sin_subfase'] : [])].map(sf => (
                  <button key={sf} onClick={() => setSubfaseFilter(sf)} style={{
                    padding: '4px 12px', fontSize: '0.75rem', fontFamily: 'inherit',
                    border: `1px solid ${subfaseFilter === sf ? ACCENT.olive : '#DDD5C8'}`,
                    background: subfaseFilter === sf ? ACCENT.olive : 'transparent',
                    color: subfaseFilter === sf ? '#FFF' : '#5C4A3A',
                    borderRadius: 4, cursor: 'pointer', fontWeight: subfaseFilter === sf ? 600 : 400,
                  }}>{sf === 'todas' ? 'Todas' : sf === 'sin_subfase' ? 'Sin subfase' : sf}</button>
                ))}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: '0.7rem', color: '#7A6A5A' }}>
                  {partidasFiltradas.length} partidas · {kpi.nGreen} verdes
                </span>
              </div>
            </div>
          )}

          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <KpiCard icon={CurrencyEur} label="CapEx total" value={fmtEur(kpi.total)}
              note={kpi.eurPerM2 != null ? `${Math.round(kpi.eurPerM2)} €/m² · ${effectiveSurfaceForKpi.toLocaleString('es-ES')} m² GFA` : 'Sin superficie'}
              color={ACCENT.amber} />
            <KpiCard icon={Leaf} label="CapEx verde" value={fmtEur(kpi.green)}
              note={`${fmtPct(kpi.greenPct)} del total · ${kpi.nGreen} partidas`}
              color={ACCENT.olive} />
            <KpiCard icon={ChartBar} label="OpEx anual real" value={opex.totalEur != null ? fmtEur(opex.totalEur) : 'Sin sensores'}
              note={opex.year
                ? `Elec ${fmtEur((opex.electricidadEur || 0) + (opex.gasEur || 0) + (opex.districtHeatEur || 0))} (${opex.elecDetail?.precioReal ? opex.elecDetail.precioReal.toFixed(4) + ' €/kWh factura' : 'precio estimado'}) · Agua ${fmtEur(opex.aguaEur || 0)} (${opex.aguaDetail?.precioReal ? opex.aguaDetail.precioReal.toFixed(2) + ' €/m³ factura' : 'precio estimado'})`
                : 'Carga building_sensors'}
              color={ACCENT.slate} />
            <KpiCard icon={TrendUp} label="Payback real" value={financial.payback != null ? `${financial.payback.toFixed(1)} años` : '—'}
              note={financial.totalSobrecoste
                ? `Sobrecoste ${fmtEur(financial.totalSobrecoste)} · ahorro ${fmtEur(financial.annualSaving)}/año · VAN10 ${fmtEur(financial.npv10)}`
                : 'Necesita partidas con retorno + OpEx real'}
              color={financial.npv10 > 0 ? ACCENT.olive : ACCENT.coral} />
          </div>

          {/* Comparativa subfases */}
          {comparativaFases && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>
                Comparativa por subfase
              </h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#EDE7DE' }}>
                    {['Subfase', 'Partidas', 'CapEx total', 'CapEx verde', '% verde'].map(h =>
                      <th key={h} style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', color: '#5C4A3A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {comparativaFases.map(r => (
                    <tr key={r.subfase} style={{ borderBottom: '1px solid #EDE7DE' }}>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600, color: '#2C2017' }}>{r.subfase}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017' }}>{r.n}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.amber }}>{fmtEur(r.total)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive }}>{fmtEur(r.green)}</td>
                      <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: '#5C4A3A' }}>{fmtPct(r.greenPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Desglose por capítulo */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>
              CapEx por capítulo constructivo
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#EDE7DE' }}>
                  {['Capítulo', 'Partidas', 'CapEx total', '% del total', 'CapEx verde', '% verde'].map(h =>
                    <th key={h} style={{ padding: '0.55rem 0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.7rem', color: '#5C4A3A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {porCapitulo.map(r => (
                  <tr key={r.capitulo} style={{ borderBottom: '1px solid #EDE7DE' }}>
                    <td style={{ padding: '0.55rem 0.75rem', color: '#2C2017' }}>{capituloLabel(r.capitulo)}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017' }}>{r.n}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.amber }}>{fmtEur(r.total)}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: '#5C4A3A' }}>{kpi.total > 0 ? fmtPct((r.total / kpi.total) * 100) : '—'}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive }}>{fmtEur(r.green)}</td>
                    <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: '#5C4A3A' }}>{r.total > 0 ? fmtPct((r.green / r.total) * 100) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#EDE7DE', fontWeight: 700 }}>
                  <td style={{ padding: '0.55rem 0.75rem' }}>TOTAL</td>
                  <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace" }}>{kpi.nTotal}</td>
                  <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.amber }}>{fmtEur(kpi.total)}</td>
                  <td>—</td>
                  <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive }}>{fmtEur(kpi.green)}</td>
                  <td style={{ padding: '0.55rem 0.75rem', fontFamily: "'IBM Plex Mono', monospace" }}>{fmtPct(kpi.greenPct)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Top sistemas */}
          {porSistema.length > 0 && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>
                Top sistemas por CapEx
              </h3>
              <ResponsiveContainer width="100%" height={Math.max(240, porSistema.length * 30)}>
                <BarChart data={porSistema} layout="vertical" margin={{ top: 5, right: 30, left: 30, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DE" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#7A6A5A', fontSize: '0.7rem' }} axisLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="sistema" tick={{ fill: '#5C4A3A', fontSize: '0.72rem' }} axisLine={false} tickLine={false} width={140} />
                  <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #DDD5C8', borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar dataKey="total" name="CapEx total" fill={ACCENT.amber} />
                  <Bar dataKey="green" name="CapEx verde" fill={ACCENT.olive} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Tabla de análisis financiero por categoría verde */}
          {financialBreakdown.rows.length > 0 && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>
                Análisis financiero por tipo de medida verde
              </h3>
              <div style={{ marginBottom: '0.75rem', fontSize: '0.72rem', color: '#7A6A5A', fontStyle: 'italic', lineHeight: 1.5 }}>
                <strong>Modelo trazable · sin proxies:</strong>
                <ul style={{ margin: '0.4rem 0 0 1.1rem', padding: 0 }}>
                  <li><strong>Benchmark base (sin medidas verdes)</strong>: <a href={idaeBenchmark?.fuenteUrl} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT.olive }}>MITECO/AVEN Guía Eficiencia Hoteles</a> · <em>{idaeBenchmark?.label}</em> · pág. 35 tabla "Parámetros de eficiencia en hoteles" · rango Pobre. Aplicado a {effectiveSurface.toLocaleString('es-ES')} m² GFA (MAT1-DATA).</li>
                  <li><strong>Consumos reales</strong>: sensores building_sensors 12 meses del año más reciente (Supabase). No se aplica ningún % teórico sobre facturas.</li>
                  <li><strong>Sobrecoste incremental verde</strong>: cotejado con <a href="https://generadordeprecios.info" target="_blank" rel="noopener noreferrer" style={{ color: ACCENT.olive }}>CYPE Generador de Precios</a> por categoría (pintura ecológica vs base 21% · LED vs fluorescente 25% · recuperador entálpico vs vent. simple 150% · SATE lana vs EPS 15%).</li>
                  {hotelMeta && (
                    <li><strong>Meta hotel</strong>: {hotelMeta.numHabitaciones} hab · {hotelMeta.plazasMaximas} plazas · ocupación {(hotelMeta.ocupacionMedia * 100).toFixed(0)}% ({hotelMeta.fuenteOcupacion}). Habitaciones: {hotelMeta.fuenteHabitaciones}.</li>
                  )}
                  <li><strong>Precios oficiales ES 2024-25</strong>: electricidad 0,18 €/kWh (PVPC REE) · gas 0,08 €/kWh (CNMC) · agua 2,50 €/m³ (media urbana Aqualia/Canal Isabel II).</li>
                </ul>
                <div style={{ marginTop: '0.4rem' }}>Las categorías sin retorno financiero directo (materiales bajo C, biodiversidad, movilidad) suman sobrecoste pero no reducen OpEx — su retorno se mide en huella C reportable (CSRD E1) y puntos BREEAM.</div>
              </div>
              {financialBreakdown.opexBaseTotal && (
                <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.85rem', background: '#F7F3EC', borderRadius: 6, fontSize: '0.75rem', color: '#5C4A3A' }}>
                  <strong>OpEx base IDAE {idaeBenchmark?.label}:</strong> {fmtEur(financialBreakdown.opexBaseTotal)}/año (elec {fmtEur(financialBreakdown.opexBaseElec)} + agua {fmtEur(financialBreakdown.opexBaseAgua)}) &nbsp;·&nbsp;
                  <strong>OpEx real medido:</strong> {fmtEur((financialBreakdown.opexRealElec || 0) + (financialBreakdown.opexRealAgua || 0))}/año &nbsp;·&nbsp;
                  <strong style={{ color: ACCENT.olive }}>Ahorro real:</strong> {fmtEur(financialBreakdown.totalAhorro)}/año (elec {fmtEur(financialBreakdown.ahorroElec)} + agua {fmtEur(financialBreakdown.ahorroAgua)})
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#EDE7DE' }}>
                      {['Tipo de medida', 'Partidas', 'CapEx', 'Sobrecoste %', 'Sobrecoste €', 'Ahorro anual', 'Payback'].map(h =>
                        <th key={h} style={{ padding: '0.55rem 0.75rem', textAlign: h === 'Tipo de medida' ? 'left' : 'right', fontWeight: 600, fontSize: '0.68rem', color: '#5C4A3A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {financialBreakdown.rows.map((r, i) => (
                      <tr key={r.criterio} style={{ borderBottom: '1px solid #EDE7DE', background: i % 2 ? '#FBF8F3' : 'transparent' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#2C2017', fontWeight: 500 }}>{r.label}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace" }}>{r.n}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#2C2017', fontFamily: "'IBM Plex Mono', monospace" }}>{fmtEur(r.capex)}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#7A6A5A', fontFamily: "'IBM Plex Mono', monospace" }}>{(r.sobrecostePct * 100).toFixed(0)}%</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: ACCENT.amber, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{fmtEur(r.sobrecoste)}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: r.ahorroAnual > 0 ? ACCENT.olive : '#B0A89E', fontFamily: "'IBM Plex Mono', monospace" }}>
                          {r.ahorroAnual > 0 ? `${fmtEur(r.ahorroAnual)}/año` : '—'}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: '#5C4A3A', fontSize: '0.72rem' }}>
                          {r.paybackCategoria != null ? `${r.paybackCategoria.toFixed(1)} años` : r.paybackTipico}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ background: '#3B2F2F', color: '#F5F0EA' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: 600, fontSize: '0.78rem' }}>TOTAL</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{financialBreakdown.rows.reduce((s, r) => s + r.n, 0)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontFamily: "'IBM Plex Mono', monospace" }}>{fmtEur(financialBreakdown.rows.reduce((s, r) => s + r.capex, 0))}</td>
                      <td />
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{fmtEur(financial.totalSobrecoste)}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>{fmtEur(financial.annualSaving)}/año</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 600 }}>{financial.payback != null ? `${financial.payback.toFixed(1)} años` : '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Gráfico VAN */}
          {opexChartData.length > 0 && (
            <div style={CARD}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>Proyección financiera 20 años</h3>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: '#5C4A3A' }}>Tasa descuento (%):</span>
                  <input type="number" value={discountRate} onChange={e => setDiscountRate(parseFloat(e.target.value) || 0)}
                    style={{ width: 60, padding: '3px 6px', border: '1px solid #DDD5C8', borderRadius: 4, fontSize: '0.8rem', fontFamily: "'IBM Plex Mono', monospace" }} />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={opexChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDE7DE" />
                  <XAxis dataKey="year" tick={{ fill: '#7A6A5A', fontSize: '0.7rem' }} axisLine={false} />
                  <YAxis tick={{ fill: '#7A6A5A', fontSize: '0.7rem' }} axisLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ background: '#FFFFFF', border: '1px solid #DDD5C8', borderRadius: 4, fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Line type="monotone" dataKey="inversion" name="Balance activo verde" stroke={ACCENT.olive} strokeWidth={2} dot={{ fill: ACCENT.olive, r: 4 }} />
                  <Line type="monotone" dataKey="referencia" name="Referencia (sin inversión)" stroke="#B0A89E" strokeWidth={2} strokeDasharray="4 4" />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: '#7A6A5A', fontStyle: 'italic', lineHeight: 1.5 }}>
                <strong>Balance = -Sobrecoste incremental verde + Σ(ahorro anual descontado)</strong>. Cada ahorro futuro se descuenta a valor presente con la fórmula VAN: ahorro / (1+tasa)^año. Cuanto mayor la tasa, más plana la curva (los ahorros futuros valen menos que hoy). El sobrecoste incremental viene de CYPE Generador de Precios y el ahorro anual = OpEx base MITECO (hotel sin medidas verdes) − OpEx real medido en sensores.
              </div>
            </div>
          )}

          {/* Partidas verdes con trazabilidad */}
          {kpi.nGreen > 0 && (
            <div style={CARD}>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', fontWeight: 600, color: '#2C2017' }}>
                Partidas verdes con justificación ({kpi.nGreen})
              </h3>
              <div style={{ marginBottom: '0.5rem', fontSize: '0.72rem', color: '#7A6A5A', fontStyle: 'italic', lineHeight: 1.4 }}>
                Cada partida marcada como sostenible incluye el criterio aplicado + la cita literal del PDF que lo justifica. Si algún criterio no te convence, avísame para reprocesar con reglas más estrictas.
              </div>
              <div style={{ overflowX: 'auto', maxHeight: 480 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#EDE7DE' }}>
                    <tr>
                      {['Código', 'Descripción', 'Importe', 'Criterio', 'Evidencia literal'].map(h =>
                        <th key={h} style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontWeight: 600, fontSize: '0.68rem', color: '#5C4A3A', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {partidasFiltradas.filter(p => p.es_sostenible)
                      .sort((a, b) => (b.importe_total_eur || 0) - (a.importe_total_eur || 0))
                      .map((p, i) => (
                        <tr key={`${p.codigo}-${i}`} style={{ borderBottom: '1px solid #EDE7DE' }}>
                          <td style={{ padding: '0.5rem 0.6rem', fontFamily: "'IBM Plex Mono', monospace", color: '#5C4A3A', whiteSpace: 'nowrap' }}>{p.codigo || '—'}</td>
                          <td style={{ padding: '0.5rem 0.6rem', color: '#2C2017', maxWidth: 320, lineHeight: 1.4 }}>{(p.descripcion || '').slice(0, 130)}{(p.descripcion || '').length > 130 ? '…' : ''}</td>
                          <td style={{ padding: '0.5rem 0.6rem', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.amber, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtEur(p.importe_total_eur)}</td>
                          <td style={{ padding: '0.5rem 0.6rem' }}>
                            {p.criterio_sostenibilidad
                              ? <span style={{ background: '#EAF3EC', color: '#3B7A50', padding: '2px 8px', borderRadius: 3, fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.criterio_sostenibilidad}</span>
                              : <span style={{ color: '#B0A89E', fontStyle: 'italic', fontSize: '0.72rem' }}>sin criterio</span>}
                          </td>
                          <td style={{ padding: '0.5rem 0.6rem', color: '#5C4A3A', fontStyle: p.evidencia_literal ? 'normal' : 'italic', maxWidth: 260, lineHeight: 1.4 }}>
                            {p.evidencia_literal || p.descripcion_sostenibilidad || '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Nota informativa sobre financiación sostenible */}
          <div style={{ ...CARD, borderLeft: `3px solid ${ACCENT.slate}` }}>
            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
              <Info size={18} style={{ color: ACCENT.slate, flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: '0.8rem', color: '#5C4A3A', lineHeight: 1.6 }}>
                <strong style={{ color: '#2C2017' }}>Financiación sostenible.</strong> El activo puede optar a instrumentos de financiación sostenible según el marco de cada emisor. Los principales estándares aplicables son: <strong>ICMA Green Bond Principles</strong> (Green Bonds), <strong>LMA Green Loan Principles</strong> (Green Loans) y <strong>Reg. (UE) 2020/852</strong> (Taxonomía UE). BREEAM aporta evidencia parcial en la mayoría de estos frameworks pero <strong>no da acceso automático</strong> — cada instrumento tiene sus propios criterios técnicos (NZEB, DNSH, alineación Taxonomía, umbrales de reducción de emisiones vs baseline). Consulta con el emisor / banco los criterios específicos aplicables.
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}

function KpiCard({ icon: Icon, label, value, note, color }) {
  return (
    <div style={{ ...CARD, borderLeft: `3px solid ${color}` }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.4rem' }}>
        <Icon size={16} style={{ color: color, flexShrink: 0 }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: '#2C2017', lineHeight: 1.15 }}>
        {value}
      </div>
      {note && <div style={{ fontSize: '0.7rem', color: '#7A6A5A', marginTop: '0.3rem', lineHeight: 1.4 }}>{note}</div>}
    </div>
  )
}
